import { notificationsClient } from 'clients/NotificationsClient/NotificationsClient';
import { fetchIndeedGraphQL } from 'shared/legacy/hooks/useBrowserExtension/indeed/utils';
import { findLast50Conversations } from './indeed.GQLOperations';
import {
  getJobSeekerKeyOnIndeedBasedOnCandidateNameProps,
  getJobSeerByMongoOrIndeedProps,
  IndeedConversationScopeKey,
} from './indeed.types';
import { filterConversationsByCandidateName, getConversationsBasedOnResponse, normalizeString } from './indeed.utils';
import { IndeedConversation } from '@/shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';

const getJobSeekerKeyOnIndeedBasedOnCandidateName = async ({
  indeedConfig,
  candidateName,
  tenantId,
  candidateId,
}: getJobSeekerKeyOnIndeedBasedOnCandidateNameProps) => {
  let previousPageToken: string | undefined;
  let numMessagesSearched = 0;
  let errors = 0;

  while (numMessagesSearched < 200 && errors < 2) {
    try {
      const response = await fetchIndeedGraphQL(findLast50Conversations(previousPageToken), indeedConfig);
      const last50Conversations = getConversationsBasedOnResponse(response);
      const filteredConversations = filterConversationsByCandidateName(last50Conversations, candidateName);

      newrelic.addPageAction('IndeedSyncStep', {
        step: 'job_seeker_name_search',
        tenantId,
        candidateId,
        candidateName: normalizeString(candidateName),
        batchIndex: numMessagesSearched / 50,
        batchSize: last50Conversations.length,
        matched: String(filteredConversations.length > 0),
        pageParticipantNames: JSON.stringify(
          last50Conversations.flatMap((c: IndeedConversation) =>
            c.participants.map((p) => normalizeString(p.participantName)),
          ),
        ),
      });

      if (filteredConversations.length > 0) {
        const jobSeekerKey = filteredConversations[0].scope.filter(
          (a) => a.key == IndeedConversationScopeKey.JOB_SEEKER_KEY,
        )[0].value;
        return jobSeekerKey;
      }

      numMessagesSearched += 50;
      const pageInfo = response.data?.findConversations?.pageInfo;

      if (!pageInfo?.hasPreviousPage) break;

      previousPageToken = pageInfo.startCursor;
    } catch (err) {
      errors++;
    }
  }

  return null;
};

export const getJobSeekerByMongoOrIndeed = async ({
  candidateId,
  candidateName,
  indeedConfig,
  tenantId,
  userEmail,
}: getJobSeerByMongoOrIndeedProps) => {
  try {
    const jobSeekerKeyOnMongo = await notificationsClient.fetchIndeedJobSeekerKey({
      tenantId: tenantId,
      candidateId: candidateId,
    });
    if (jobSeekerKeyOnMongo.data.job_seeker_keys.length == 0) {
      const jobSeekerKeyOnIndeed = await getJobSeekerKeyOnIndeedBasedOnCandidateName({
        indeedConfig,
        candidateName: candidateName,
        tenantId: tenantId,
        candidateId: candidateId,
      });

      if (jobSeekerKeyOnIndeed && userEmail) {
        await notificationsClient.createdIndeedJobSeekerKey({
          tenantId: tenantId,
          candidateId: candidateId,
          jobSeekerKey: jobSeekerKeyOnIndeed,
          credentialUsername: userEmail,
        });
      }

      return jobSeekerKeyOnIndeed;
    } else {
      return jobSeekerKeyOnMongo.data.job_seeker_keys[0].job_seeker_key;
    }
  } catch (err) {
    console.error(err);
  }
};
